import { useCallback, useState } from "react";
import { IssueUpdateSheet } from "@/components/issues/issue-update-sheet/issue-update-sheet";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import type {
  IssueStatusAccess,
  IssueStatusAccessFor,
} from "@/hooks/use-issue-status-access";
import { useMediaQuery } from "@/hooks/use-media-query";
import type { ProjectIssue } from "@/types/api";

// Six columns need room beside the 240px filter sidebar; below this the list
// keeps its two-line cards. See [[issue-list-frame.spec]].
export const TABLE_QUERY = "(min-width: 1280px)";

/** The issue whose update sheet is open, and what the viewer may post on it. */
type StatusTarget = IssueStatusAccess & { issue: ProjectIssue };

interface UseIssueRowsOptions {
  projectId: string;
  /**
   * What this viewer may do to each issue's status. When it returns access,
   * the row offers the update sheet. Omitted, no row offers it.
   */
  statusAccessFor?: IssueStatusAccessFor;
  /** Selection mode: the update shortcuts are off for every row. */
  selecting?: boolean;
  /**
   * Render the two-line cards even when the window is wide enough for the
   * table — an open side panel leaves the list too narrow for six columns.
   */
  forceCards?: boolean;
}

/**
 * The behavior every list of issues shares — the Issues page and an issues
 * report alike: table or cards, how a row offers a status change, and the one
 * sheet that posts it. See [[issue-list-frame.spec]].
 */
export function useIssueRows({
  projectId,
  statusAccessFor,
  selecting = false,
  forceCards = false,
}: UseIssueRowsOptions) {
  const wideEnoughForTable = useMediaQuery(TABLE_QUERY);
  const asTable = wideEnoughForTable && !forceCards;
  // The swipe is a thumb gesture, so phones get it and desktop gets the status
  // pill — the same 768px line that decides whether a row opens in place.
  const isDesktop = useIsDesktop();

  // One sheet for the whole list, bound to whichever row asked for it. The
  // target outlives `open` so the sheet can animate out with its content.
  const [statusTarget, setStatusTarget] = useState<StatusTarget | null>(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const openStatus = useCallback(
    (issue: ProjectIssue) => {
      const access = statusAccessFor?.(issue);
      if (!access) return;
      setStatusTarget({ ...access, issue });
      setStatusOpen(true);
    },
    [statusAccessFor],
  );
  // Selecting switches the update shortcuts off for every row.
  const updateHandlerFor = (issue: ProjectIssue) =>
    !selecting && statusAccessFor?.(issue) ? openStatus : undefined;

  // Mount this beside every state the list can be in, not just the rows: an
  // update can empty the tab it was opened from, and the sheet must not vanish
  // mid-close when the list swaps to its empty state. The sheet confirms its
  // own update with a toast, so there is nothing to wire up here.
  const statusSheet = statusTarget ? (
    <IssueUpdateSheet
      projectId={projectId}
      issue={statusTarget.issue}
      open={statusOpen}
      onOpenChange={setStatusOpen}
      canClose={statusTarget.canClose}
      canUpdate={statusTarget.canUpdate}
      mode={statusTarget.action}
      presentation={isDesktop ? "dialog" : "drawer"}
      showIssue
    />
  ) : null;

  return {
    asTable,
    updateStatusVia: isDesktop ? ("chip" as const) : ("swipe" as const),
    updateHandlerFor,
    statusSheet,
  };
}
